import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, ArrowLeft, Inbox, FileText, Layers, Upload, CalendarClock, ListOrdered, ChevronDown, Info } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useLanguage, CONTACT } from "@/i18n";
import LogoMark from "@/components/Logo";
import AdminSections from "@/components/AdminSections";
import AdminSettings from "@/components/AdminSettings";
import AdminBookings from "@/components/AdminBookings";
import AdminMenu from "@/components/AdminMenu";
import { api, formatApiErrorDetail } from "@/lib/api";

const EMPTY_FORM = {
  title: "",
  title_en: "",
  category: "",
  excerpt: "",
  excerpt_en: "",
  content: "",
  content_en: "",
  cover_image: "",
  published_at: "",
};

function ArticleForm({ open, onOpenChange, article, onSaved }) {
  const { t } = useLanguage();
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sectionOptions, setSectionOptions] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    if (open) {
      api.get("/sections").then((res) => setSectionOptions(res.data)).catch(() => {});
    }
  }, [open]);

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, cover_image: res.data.url }));
      toast.success("Immagine caricata.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  useEffect(() => {
    if (article) {
      setForm({
        ...EMPTY_FORM,
        ...article,
        published_at: (article.published_at || "").slice(0, 10),
      });
      setShowEn(Boolean(article.title_en || article.excerpt_en || article.content_en));
    } else {
      setForm({ ...EMPTY_FORM, published_at: new Date().toISOString().slice(0, 10) });
      setShowEn(false);
    }
  }, [article, open]);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const [showEn, setShowEn] = useState(false);

  const save = async () => {
    const missing =
      form.title.trim().length < 3
        ? "Il titolo è obbligatorio (almeno 3 caratteri)."
        : form.category.trim().length < 2
          ? "La categoria è obbligatoria (almeno 2 caratteri)."
          : form.excerpt.trim().length < 10
            ? "L'occhiello è obbligatorio: scrivi una breve sintesi dell'articolo (almeno 10 caratteri)."
            : form.content.trim().length < 20
              ? "Il contenuto è obbligatorio: scrivi il testo dell'articolo (almeno 20 caratteri)."
              : null;
    if (missing) {
      toast.error(missing);
      return;
    }
    setSaving(true);
    try {
      if (article?.id) {
        await api.put(`/admin/articles/${article.id}`, form);
      } else {
        await api.post("/admin/articles", form);
      }
      toast.success(t.admin.saved);
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const fieldsIt = [
    { key: "title", label: t.admin.title, type: "input", required: true, maxLength: 200 },
    { key: "category", label: t.admin.category, type: "input", required: true, maxLength: 60 },
    { key: "section_slug", label: "Sezione / Section", type: "select", options: [{ value: "", label: "— nessuna / none —" }, ...sectionOptions.map((s) => ({ value: s.slug, label: s.title }))]},
    { key: "published_at", label: t.admin.date, type: "date" },
    { key: "excerpt", label: `${t.admin.excerpt} *`, type: "textarea", required: true, maxLength: 600 },
    { key: "content", label: `${t.admin.content} *`, type: "textarea", rows: 8, required: true },
  ];

  const fieldsEn = [
    { key: "title_en", label: t.admin.titleEn, type: "input", maxLength: 200 },
    { key: "excerpt_en", label: t.admin.excerptEn, type: "textarea", maxLength: 600 },
    { key: "content_en", label: t.admin.contentEn, type: "textarea", rows: 6 },
  ];

  const renderField = (f) =>
    f.type === "textarea" ? (
      <Textarea
        data-testid={`article-form-${f.key}`}
        rows={f.rows || 3}
        maxLength={f.maxLength}
        required={f.required}
        value={form[f.key] || ""}
        onChange={set(f.key)}
        className="mt-1.5"
      />
    ) : f.type === "select" ? (
      <select
        data-testid={`article-form-${f.key}`}
        value={form[f.key] || ""}
        onChange={set(f.key)}
        className="mt-1.5 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
      >
        {f.options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    ) : (
      <Input
        data-testid={`article-form-${f.key}`}
        type={f.type === "date" ? "date" : "text"}
        maxLength={f.maxLength}
        required={f.required}
        value={form[f.key] || ""}
        onChange={set(f.key)}
        className="mt-1.5"
      />
    );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            {article ? t.admin.editArticle : t.admin.newArticle}
          </DialogTitle>
        </DialogHeader>
        <div
          data-testid="article-form-guide"
          className="flex items-start gap-2 rounded-md border border-brand/20 bg-brand-linen/50 p-3.5"
        >
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" />
          <p className="text-xs font-light leading-relaxed text-slate-600">{t.admin.guideText}</p>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{t.admin.cover}</label>
            <div className="mt-1.5 flex gap-2">
              <Input
                data-testid="article-form-cover_image"
                value={form.cover_image || ""}
                onChange={set("cover_image")}
                placeholder="https://… o carica dal computer"
              />
              <label
                data-testid="article-form-upload-label"
                className="shrink-0 inline-flex cursor-pointer items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-xs font-medium text-brand hover:border-brand transition-colors duration-300"
              >
                <Upload className="h-4 w-4" />
                {uploading ? "…" : "Carica"}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={uploadFile}
                  data-testid="article-form-cover-file"
                />
              </label>
            </div>
            {form.cover_image && (
              <img
                src={form.cover_image}
                alt="Anteprima copertina"
                data-testid="article-form-cover-preview"
                className="mt-3 h-24 w-full object-cover"
              />
            )}
          </div>
          {fieldsIt.map((f) => (
            <div key={f.key}>
              <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{f.label}</label>
              {renderField(f)}
            </div>
          ))}
          <div className="border-t border-slate-100 pt-4">
            <button
              type="button"
              data-testid="article-form-en-toggle"
              onClick={() => setShowEn(!showEn)}
              className="flex w-full items-center justify-between gap-3 text-[11px] uppercase tracking-[0.2em] text-slate-500 hover:text-brand transition-colors duration-300"
            >
              {showEn ? t.admin.enSection : t.admin.enSectionAdd}
              <ChevronDown className={`h-4 w-4 shrink-0 transition-transform duration-300 ${showEn ? "rotate-180" : ""}`} />
            </button>
            {showEn && (
              <div className="mt-4 space-y-4">
                {fieldsEn.map((f) => (
                  <div key={f.key}>
                    <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{f.label}</label>
                    {renderField(f)}
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" data-testid="article-form-cancel" onClick={() => onOpenChange(false)}>
              {t.admin.cancel}
            </Button>
            <Button
              data-testid="article-form-save"
              onClick={save}
              disabled={saving}
              className="bg-brand hover:bg-brand-dark"
            >
              {t.admin.save}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Admin() {
  const { t } = useLanguage();
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState("articoli");
  const [articles, setArticles] = useState([]);
  const [messages, setMessages] = useState([]);
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    api.get("/auth/me")
      .then((res) => setUser(res.data))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/admin/articles").then((res) => setArticles(res.data)).catch(() => {});
    api.get("/admin/messages").then((res) => setMessages(res.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    const onSessionDead = () => setUser(null);
    window.addEventListener("auth:logout", onSessionDead);
    return () => window.removeEventListener("auth:logout", onSessionDead);
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    try {
      await api.post("/auth/login", credentials);
      const me = await api.get("/auth/me");
      setUser(me.data);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const removeArticle = async (article) => {
    if (!window.confirm(t.admin.confirmDelete)) return;
    await api.delete(`/admin/articles/${article.id}`);
    setArticles(articles.filter((a) => a.id !== article.id));
    toast.success(t.admin.deleted);
  };

  const removeMessage = async (m) => {
    await api.delete(`/admin/messages/${m.id}`).catch(() => {});
    setMessages(messages.filter((x) => x.id !== m.id));
  };

  const refreshArticles = async () => {
    const res = await api.get("/admin/articles");
    setArticles(res.data);
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-brand-linen">
      <header className="bg-white border-b border-slate-200">
        <div className="container-editorial h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-back-to-site">
            <LogoMark className="h-9 w-auto" />
            <span className="flex items-center gap-2 text-sm text-slate-500">
              <ArrowLeft className="h-4 w-4" /> Torna al sito
            </span>
          </Link>
          {user && (
            <Button
              variant="outline"
              data-testid="admin-logout-button"
              onClick={logout}
              className="gap-2 text-slate-600"
            >
              <LogOut className="h-4 w-4" /> {t.admin.logout}
            </Button>
          )}
        </div>
      </header>

      <div className="container-editorial py-12 max-w-4xl">
        {user === undefined && <p className="text-sm text-slate-500">{t.admin.loading}</p>}

        {user === null && (
          <div className="max-w-md mx-auto bg-white border border-slate-200 p-8 sm:p-10">
            <h1 className="font-serif text-3xl text-slate-900">{t.admin.login}</h1>
            <p className="mt-2 text-sm font-light text-slate-500">{t.admin.loginSub}</p>
            <form onSubmit={login} className="mt-8 space-y-4" data-testid="admin-login-form">
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{t.admin.email}</label>
                <Input
                  data-testid="admin-login-email"
                  type="email"
                  required
                  value={credentials.email}
                  onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{t.admin.password}</label>
                <Input
                  data-testid="admin-login-password"
                  type="password"
                  required
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="mt-1.5"
                />
              </div>
              {loginError && (
                <p className="text-sm text-red-600" data-testid="admin-login-error">
                  {loginError}
                </p>
              )}
              <Button
                type="submit"
                data-testid="admin-login-submit"
                className="w-full bg-brand hover:bg-brand-dark"
              >
                {t.admin.signIn}
              </Button>
            </form>
          </div>
        )}

        {user && (
          <div>
            <AdminSettings />
            <div className="flex items-center justify-between">
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="admin-tab-menu"
                  onClick={() => setTab("menu")}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                    tab === "menu" ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600"
                  }`}
                >
                  <ListOrdered className="h-4 w-4" /> Menu
                </button>
                <button
                  data-testid="admin-tab-sezioni"
                  onClick={() => setTab("sezioni")}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                    tab === "sezioni" ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600"
                  }`}
                >
                  <Layers className="h-4 w-4" /> {t.admin.section.tab}
                </button>
                <button
                  data-testid="admin-tab-articoli"
                  onClick={() => setTab("articoli")}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                    tab === "articoli" ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600"
                  }`}
                >
                  <FileText className="h-4 w-4" /> {t.admin.tabArticles}
                </button>
                <button
                  data-testid="admin-tab-prenotazioni"
                  onClick={() => setTab("prenotazioni")}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                    tab === "prenotazioni" ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600"
                  }`}
                >
                  <CalendarClock className="h-4 w-4" /> Prenotazioni / Bookings
                </button>
                <button
                  data-testid="admin-tab-messaggi"
                  onClick={() => setTab("messaggi")}
                  className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                    tab === "messaggi" ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600"
                  }`}
                >
                  <Inbox className="h-4 w-4" /> {t.admin.tabMessages}
                  {messages.length > 0 && (
                    <span className="rounded-full bg-brand-soft px-2 py-0.5 text-[10px] text-brand">
                      {messages.length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {tab === "articoli" && (
              <div className="mt-8 space-y-3">
                <div className="flex justify-end">
                  <Button
                    data-testid="admin-new-article-button"
                    onClick={() => {
                      setEditing(null);
                      setFormOpen(true);
                    }}
                    className="gap-2 bg-brand hover:bg-brand-dark"
                  >
                    <Plus className="h-4 w-4" /> {t.admin.newArticle}
                  </Button>
                </div>
                {articles.map((a) => (
                  <div
                    key={a.id}
                    data-testid="admin-article-row"
                    className="flex items-center justify-between gap-4 bg-white border border-slate-200 p-5"
                  >
                    <div className="min-w-0">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-brand">{a.category}</p>
                      <p className="mt-1 font-serif text-lg text-slate-900 truncate">{a.title}</p>
                      <p className="text-xs font-light text-slate-400">{a.published_at?.slice(0, 10)}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="icon"
                        data-testid={`admin-edit-article-${a.slug}`}
                        onClick={() => {
                          setEditing(a);
                          setFormOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        data-testid={`admin-delete-article-${a.slug}`}
                        onClick={() => removeArticle(a)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "messaggi" && (
              <div className="mt-8 space-y-3">
                {messages.length === 0 && (
                  <p className="text-sm font-light text-slate-500">{t.admin.noMessages}</p>
                )}
                {messages.map((m) => (
                  <div
                    key={m.id}
                    data-testid="admin-message-row"
                    className="bg-white border border-slate-200 p-5"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{m.name}</p>
                        <p className="text-xs font-light text-slate-500">
                          {m.email} {m.phone ? `· ${m.phone}` : ""}
                        </p>
                        <p className="mt-3 text-sm font-light leading-relaxed text-slate-700">{m.message}</p>
                        <p className="mt-3 text-[10px] uppercase tracking-[0.2em] text-slate-400">
                          {m.created_at?.slice(0, 16).replace("T", " ")}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="icon"
                        data-testid={`admin-delete-message-${m.id}`}
                        onClick={() => removeMessage(m)}
                        className="text-red-600 hover:text-red-700 shrink-0"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {tab === "sezioni" && <AdminSections />}
            {tab === "prenotazioni" && <AdminBookings />}
            {tab === "menu" && <AdminMenu />}
          </div>
        )}
      </div>

      <ArticleForm
        open={formOpen}
        onOpenChange={setFormOpen}
        article={editing}
        onSaved={refreshArticles}
      />
    </div>
  );
}
